import { useContext } from 'react'
import { useParams } from 'react-router'

import {
  ProfileWidgetsExpandContext,
  ProfileWidgetsHideContext,
  Screen,
} from '../../../context'
import { PageWithWidgets } from '../PageWithWidgets'

import { DesktopActionsWidget } from './DesktopActionsWidget'
import {
  useEntityPageProfileLayoutWidgetsExpand,
  useEntityPageProfileLayoutWidgetsHide,
} from './EntityPageProfileLayout.hooks'
import { getEntityPageProfileLayoutEntityKey } from './EntityPageProfileLayout.utils'

function EntityPageProfileLayout({
  Header,
  Loader,
  actionsWidget,
  children,
  headerProps,
  initialWidgetsHidden = false,
  isEmbedded = false,
  widgets,
  ...restProps
}) {
  // Read viewport-based size directly from the Screen context instead of
  // useScreen(): this component decides whether to render the widgets
  // sidebar, and the sidebar is sibling of the scroll wrapper measured by
  // the surrounding ScreenContainerRef. Using the container width here
  // creates a feedback loop — toggling the sidebar changes the scroll
  // wrapper width, which flips the breakpoint, which toggles the sidebar
  // again. Viewport width is the only stable input for this decision.
  const { isMobileScreenSize } = useContext(Screen)

  const params = useParams()

  const entityKey = getEntityPageProfileLayoutEntityKey(params)

  const hasActionsAndWidgets = Boolean(actionsWidget && widgets)

  const { widgetsExpand, widgetsKey } = useEntityPageProfileLayoutWidgetsExpand(
    { entityKey, isAvailable: hasActionsAndWidgets }
  )

  const isInlineWidgets = isEmbedded || isMobileScreenSize

  const { isWidgetsHidden, widgetsHide } =
    useEntityPageProfileLayoutWidgetsHide({
      entityKey,
      initialWidgetsHidden,
      isAvailable: hasActionsAndWidgets && !isInlineWidgets,
    })

  const scopedActionsWidget = (
    <ProfileWidgetsExpandContext value={widgetsExpand}>
      <ProfileWidgetsHideContext value={widgetsHide}>
        {actionsWidget}
      </ProfileWidgetsHideContext>
    </ProfileWidgetsExpandContext>
  )

  const scopedWidgets = widgets ? (
    <ProfileWidgetsExpandContext key={widgetsKey} value={widgetsExpand}>
      {widgets}
    </ProfileWidgetsExpandContext>
  ) : null

  const desktopWidgets = actionsWidget ? (
    <DesktopActionsWidget
      actionsWidget={scopedActionsWidget}
      isWidgetsHidden={isWidgetsHidden}
      widgets={scopedWidgets}
    />
  ) : (
    scopedWidgets
  )

  return (
    <>
      {Loader ? <Loader /> : null}
      <PageWithWidgets
        {...restProps}
        widgetsFlexible
        isWidgetsHidden={isWidgetsHidden}
        widgets={isInlineWidgets ? null : desktopWidgets}
      >
        {isInlineWidgets ? scopedActionsWidget : null}
        {Header ? <Header {...headerProps} /> : null}
        {isInlineWidgets ? scopedWidgets : null}

        {children}
      </PageWithWidgets>
    </>
  )
}

export default EntityPageProfileLayout
